import Button from 'components/UI/V3/Button/Button';
import React from 'react';

import styles from './TotalClientsBar.module.scss';

interface TotalClientsBarProps {
    /**
     * Computed total clients matching the selected criteria minus the manual
     * exclusions. `null` when the count isn't computable yet (counters still
     * loading, or a maqueta criteria without backend data) — renders as `-`.
     */
    total: number | null;
    /**
     * How many clients the user has manually excluded via the Edit picker.
     * Surfaced as a small hint under the total so the user understands why
     * the number is lower than they might expect from the picker.
     */
    excludedCount: number;
    /**
     * Estimated cost (subtotal + tax, in USD) for the current recipient total
     * — comes from `quote.total` fetched by the parent. `null` while no quote
     * is loaded (total is null/0 or the request is in flight on first change);
     * the cost line is hidden in that case rather than showing 0.
     */
    estimatedCost?: number | null;
    /**
     * Copy strings driven by the parent so this component stays presentational
     * — the noun ("clients" / "leads" / "recipients") depends on the currently
     * selected recipient mode and only the parent knows that context.
     */
    entityLabel: {
        /** Section title, e.g. "Total clients" / "Total leads". */
        title: string;
        /** One-line hint under the title, e.g. "View and edit the leads…". */
        description: string;
        /** Suffix rendered after the count number, e.g. "Clients" / "Leads". */
        count: string;
    };
    /**
     * Opens the EditClientsDrawer. The parent gates this against the same
     * criteria the backend supports (see `editDisabled`).
     */
    onEditClick: () => void;
    /**
     * True when the current campaign criteria cannot be resolved by the
     * backend yet (any maqueta sub-criteria, or `by_tag` without tags
     * selected). The button stays visible per design but is non-interactive.
     */
    editDisabled: boolean;
}

/**
 * Footer bar of step 1. Shows the total clients that will receive the email
 * (criteria filter minus manual exclusions) and exposes the Edit picker for
 * fine-grained exclusion.
 */
const TotalClientsBar: React.FC<TotalClientsBarProps> = ({
    total,
    excludedCount,
    estimatedCost,
    entityLabel,
    onEditClick,
    editDisabled
}) => {
    return (
        <div className={styles.section}>
            <h3 className={styles.title}>{entityLabel.title}</h3>
            <p className={styles.description}>{entityLabel.description}</p>

            <div className={styles.row}>
                <div className={styles.totalGroup}>
                    {total != null ? (
                        <>
                            <div className={styles.totalLine}>
                                <span className={styles.number}>{total}</span>
                                <span className={styles.label}> {entityLabel.count}</span>
                                {excludedCount > 0 && (
                                    <span className={styles.excludedHint}>{excludedCount} excluded</span>
                                )}
                            </div>
                            {estimatedCost != null && (
                                <span className={styles.estimatedCost}>
                                    Estimated cost: ${estimatedCost.toFixed(2)}
                                </span>
                            )}
                        </>
                    ) : (
                        <span className={styles.placeholder}>—</span>
                    )}
                </div>

                <Button
                    size="medium"
                    label="Edit"
                    className={styles.editButton}
                    onClick={onEditClick}
                    disabled={editDisabled}
                    variant="black"
                />
            </div>
        </div>
    );
};

export default TotalClientsBar;
